/**
 * useChecklistAcceptance - Hooks do Core de Aceites (consumidor Checklist)
 *
 * - Preparação: `prepare_checklist_acceptance` resolve no servidor política, termo,
 *   participantes e conteúdo protegido. O frontend informa apenas a vistoria.
 * - Confirmação: Edge Function `acceptance-confirm` (JWT + IP/UA do servidor).
 * - Configuração: políticas do tenant e configuração de aceite do modelo.
 *
 * @version 1.4.0
 * RELEASE: 2026-10-13 v1.4.0 - Aceite Titular/Testemunha: signer_kind no envio e nos dados do
 *          signatário, allow_witness na preparação e na configuração do modelo (sem RPC nova).
 * RELEASE: 2026-10-01 v1.3.0 - useAcceptanceArtifactUrls: imagens da assinatura/selfie para auditoria.
 * RELEASE: 2026-10-01 v1.2.0 - Requisito birth_date, signer_birth_date no envio e dados do signatário
 *          (participant.signer) vindos de prepare_checklist_acceptance v1.3.0 (sem RPC nova).
 * RELEASE: 2026-09-27 v1.1.0 - Situação e download do comprovante em PDF (Fase 4).
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 3).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useChecklistAcceptance');
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any)(name, args);

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export type AcceptanceRole = 'executor' | 'reviewer' | 'owner';
export type AcceptanceStage = 'before_submit' | 'before_approve' | 'before_complete';

export interface AcceptanceRequirements {
  signature_drawing: boolean;
  selfie: boolean;
  geolocation: boolean;
  document_cpf: boolean;
  /** v1.2.0: exigir data de nascimento do signatário */
  birth_date?: boolean;
}

/** v1.2.0: dados registrados no aceite (somente contexto interno) */
export interface AcceptanceSigner {
  name: string;
  document_masked: string | null;
  birth_date: string | null;
  /** v1.4.0: quem assinou o aceite do cliente (NULL = aceites antigos/demais papéis) */
  signer_kind?: 'owner' | 'witness' | null;
  asserted_at: string;
  channel: 'internal' | 'public_link' | 'acceptance_link' | null;
  actor_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  geo: { latitude: number; longitude: number; accuracy?: number | null } | null;
  assertion_hash: string;
  content_hash: string;
  artifacts: { kind: 'signature_drawing' | 'selfie'; sha256: string; size_bytes: number }[];
}

export interface AcceptanceParticipant {
  id: string;
  role: AcceptanceRole;
  required_stage: AcceptanceStage;
  is_required: boolean;
  status: string;
  user_name: string | null;
  accepted_at: string | null;
  signer_name: string | null;
  /** Formato do momento (v1.1.0 do servidor); ausente => usa o padrão da solicitação */
  requirements?: AcceptanceRequirements | null;
  terms?: { title: string; body: string; declaration: string; version_number: number } | null;
  /** v1.2.0: dados de quem assinou (ausente no link externo) */
  signer?: AcceptanceSigner | null;
}

export interface PreparedAcceptance {
  required: boolean;
  request_id?: string;
  status?: string;
  content_hash?: string;
  purpose?: string;
  /** v1.4.0: modelo permite aceite do cliente por testemunha (congelado na versão) */
  allow_witness?: boolean;
  requirements?: AcceptanceRequirements;
  terms?: { title: string; body: string; declaration: string; version_number: number };
  participants?: AcceptanceParticipant[];
}

export interface AcceptancePolicy {
  id: string;
  code: string;
  name: string;
  purpose: string;
  is_active: boolean;
  is_system: boolean;
  version: { version_number: number; requirements: AcceptanceRequirements } | null;
  terms: { version_number: number; title: string; body: string; declaration: string } | null;
}

export const ROLE_LABEL: Record<AcceptanceRole, string> = {
  executor: 'Vistoriador',
  reviewer: 'Revisor',
  owner: 'Cliente / proprietário',
};
export const STAGE_LABEL: Record<AcceptanceStage, string> = {
  before_submit: 'Antes de enviar para revisão',
  before_approve: 'Antes de concluir a revisão',
  before_complete: 'Antes de concluir a vistoria',
};
/** v1.4.0: rótulo de quem assinou o aceite do cliente */
export const SIGNER_KIND_LABEL: Record<'owner' | 'witness', string> = {
  owner: 'Titular (Cliente)',
  witness: 'Testemunha',
};
export const PURPOSE_LABEL: Record<string, string> = {
  acknowledgement: 'Ciência',
  declaration: 'Declaração',
  approval: 'Aprovação',
  authorization: 'Autorização',
};

// -----------------------------------------------------------------------------
// Vistoria: preparar e confirmar
// -----------------------------------------------------------------------------
export function useChecklistAcceptance(instanceId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['checklist-acceptance', instanceId],
    enabled: !!instanceId && enabled,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await rpc('prepare_checklist_acceptance', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as PreparedAcceptance;
    },
  });
}

export interface ConfirmAcceptanceInput {
  request_id: string;
  participant_id: string;
  idempotency_key: string;
  signer_name: string;
  signer_document?: string | null;
  /** v1.2.0: AAAA-MM-DD */
  signer_birth_date?: string | null;
  /** v1.4.0: titular/testemunha (somente aceite do cliente; validado no servidor) */
  signer_kind?: 'owner' | 'witness' | null;
  geo?: { latitude: number; longitude: number; accuracy?: number | null; captured_at_client?: string; permission_status?: string } | null;
  geo_result?: 'granted' | 'denied' | 'unavailable' | 'not_requested';
  device?: Record<string, string>;
  artifacts: { kind: 'signature_drawing' | 'selfie'; data_base64: string }[];
}

export function useConfirmAcceptance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ConfirmAcceptanceInput) => {
      const { data, error } = await supabase.functions.invoke('acceptance-confirm', { body: input });
      if (error) {
        // Extrai a mensagem de negócio devolvida pela Edge Function
        let msg = error.message;
        try { msg = (await (error as any).context?.json())?.error ?? msg; } catch { /* mantém mensagem padrão */ }
        throw new Error(msg);
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-acceptance'] });
      qc.invalidateQueries({ queryKey: ['inspection-timeline'] });
      toast.success('Aceite registrado');
    },
    onError: (e: Error) => {
      logger.warn('Falha ao registrar aceite', e.message);
      toast.error(e.message || 'Não foi possível registrar o aceite');
    },
  });
}

// -----------------------------------------------------------------------------
// Configuração: políticas e modelo
// -----------------------------------------------------------------------------
export function useAcceptancePolicies() {
  return useQuery({
    queryKey: ['acceptance-policies'],
    queryFn: async () => {
      const { data, error } = await rpc('list_acceptance_policies', { p_context_key: 'checklist_instance', p_tenant_hint: null });
      if (error) throw error;
      return (data ?? []) as AcceptancePolicy[];
    },
  });
}

export function useUpsertAcceptancePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string | null; data: Record<string, unknown> }) => {
      const { data: res, error } = await rpc('upsert_acceptance_policy', { p_data: data, p_policy_id: id ?? null, p_tenant_hint: null });
      if (error) throw error;
      return res as string;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['acceptance-policies'] }); toast.success('Política salva'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível salvar a política'),
  });
}

export interface TemplateAcceptanceConfig {
  policy_id: string;
  policy_name: string;
  is_enabled: boolean;
  /** v1.4.0: permite que o aceite do cliente seja assinado por uma testemunha */
  allow_witness?: boolean;
  /** policy_id opcional = formato próprio do momento; ausente usa a política padrão */
  participants: { role: AcceptanceRole; required_stage: AcceptanceStage; required: boolean; policy_id?: string }[];
}

export function useTemplateAcceptanceConfig(templateId: string | undefined) {
  return useQuery({
    queryKey: ['checklist-template-acceptance', templateId],
    enabled: !!templateId,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_template_acceptance_config', { p_template_id: templateId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? null) as TemplateAcceptanceConfig | null;
    },
  });
}

export function useSaveTemplateAcceptanceConfig(templateId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const { error } = await rpc('upsert_checklist_template_acceptance_config', { p_template_id: templateId, p_data: data, p_tenant_hint: null });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-template-acceptance', templateId] });
      qc.invalidateQueries({ queryKey: ['checklist-template', templateId] });
      toast.success('Aceite do modelo salvo — vale após publicar nova versão');
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível salvar o aceite'),
  });
}

// -----------------------------------------------------------------------------
// Comprovante (Fase 4)
// -----------------------------------------------------------------------------
export interface AcceptanceReceipt { request_id: string; status: 'pending' | 'processing' | 'ready' | 'failed'; final_hash: string | null }

export function useChecklistAcceptanceReceipt(instanceId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['checklist-acceptance-receipt', instanceId],
    enabled: !!instanceId && enabled,
    // Enquanto o PDF é gerado, consulta a cada 5 s; pronto/falha encerra
    refetchInterval: (q) => (['pending', 'processing'].includes((q.state.data as AcceptanceReceipt | null)?.status ?? '') ? 5000 : false),
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_acceptance_receipt', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? null) as AcceptanceReceipt | null;
    },
  });
}

export async function downloadAcceptanceReceipt(instanceId: string) {
  const { data, error } = await supabase.functions.invoke('acceptance-receipt-download', { body: { instance_id: instanceId } });
  if (error || !data?.url) {
    logger.warn('Falha ao baixar comprovante', error?.message);
    toast.error('Comprovante ainda não disponível');
    return;
  }
  window.open(data.url, '_blank', 'noopener,noreferrer');
}

/**
 * v1.3.0: URLs assinadas (5 min) da assinatura desenhada e da selfie de um participante,
 * para auditoria do revisor. Permissão revalidada no servidor (acceptance-receipt-download).
 */
export interface AcceptanceArtifactUrl { kind: 'signature_drawing' | 'selfie'; sha256: string; url: string | null }
export function useAcceptanceArtifactUrls(instanceId: string | null | undefined, participantId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['acceptance-artifact-urls', instanceId, participantId],
    enabled: !!instanceId && enabled,
    staleTime: 4 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('acceptance-receipt-download', {
        body: { mode: 'artifact', instance_id: instanceId, participant_id: participantId },
      });
      if (error) throw error;
      return ((data?.artifacts ?? []) as AcceptanceArtifactUrl[]);
    },
  });
}
